import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TiptapImage from '@tiptap/extension-image';
import { Node, mergeAttributes } from '@tiptap/core';
import { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, FileText, Columns2, Columns3, Columns4, Underline as UnderlineIcon, Strikethrough, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

// TipTap custom columns node: <div class="em-columns" data-cols="N"><div class="em-col">…</div></div>
const Columns = Node.create({
  name: 'columns',
  group: 'block',
  content: 'block+',
  defining: true,
  addAttributes() { return { cols: { default: 2 } }; },
  parseHTML() {
    return [{
      tag: 'div.em-columns',
      getAttrs: (el) => ({ cols: parseInt(el.getAttribute('data-cols') || '2', 10) }),
    }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'em-columns', 'data-cols': HTMLAttributes.cols }), 0];
  },
});

// TipTap custom node for embedding the editorial team org-chart placeholder.
// Serializes to <div class="em-imprint-tree" data-widget="imprint-tree"></div>.
// The public StaticPage component swaps this marker for the rendered
// <ImprintTreeWidget /> at exactly this position in the body.
const ImprintTree = Node.create({
  name: 'imprintTree',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML() { return [{ tag: 'div.em-imprint-tree' }]; },
  renderHTML() {
    return ['div', { class: 'em-imprint-tree', 'data-widget': 'imprint-tree' }];
  },
  addNodeView() {
    return () => {
      const dom = document.createElement('div');
      dom.className = 'em-imprint-tree';
      dom.setAttribute('data-widget', 'imprint-tree');
      dom.setAttribute('contenteditable', 'false');
      dom.style.cssText = 'border:2px dashed #ef4444;border-radius:8px;padding:14px 18px;text-align:center;background:rgba(239,68,68,.08);color:#fca5a5;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin:1em 0;cursor:default;user-select:none;';
      dom.textContent = '◆ Widget tima / Impressum (renderira se javno) ◆';
      return { dom };
    };
  },
});

export default function RichTextEditor({ initialContent, onChange, onInsertImage, onEditorReady, enablePdf = false, enableColumns = false, enableImprintWidget = false, getAuthToken }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-500 underline' } }),
      TiptapImage,
      ...(enableColumns ? [Columns] : []),
      ...(enableImprintWidget ? [ImprintTree] : []),
    ],
    content: initialContent || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => { if (editor && onEditorReady) onEditorReady(editor); }, [editor, onEditorReady]);
  // Re-load content when initialContent changes (e.g. after fetch)
  useEffect(() => {
    if (editor && initialContent && editor.getHTML() !== initialContent) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  if (!editor) return null;

  const Btn = ({ onClick, active, children, title }) => (
    <button type="button" onClick={onClick} title={title}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'}`}>
      {children}
    </button>
  );

  async function handlePdfUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = getAuthToken ? await getAuthToken() : '';
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/admin/media/upload-pdf`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
    });
    if (r.ok) {
      const { url, filename, size_bytes } = await r.json();
      const sizeKB = Math.round(size_bytes / 1024);
      const fullUrl = url.startsWith('/api/') ? `${BACKEND_HOST}${url}` : url;
      const html = `<p><a class="pdf-attachment" href="${fullUrl}" target="_blank" rel="noopener">📄 ${filename} (${sizeKB} KB)</a></p>`;
      editor.chain().focus().insertContent(html).run();
    }
    e.target.value = '';
  }

  function insertColumns(n) {
    const colTpl = Array(n).fill('<div class="em-col"><p>&nbsp;</p></div>').join('');
    const html = `<div class="em-columns" data-cols="${n}">${colTpl}</div><p></p>`;
    editor.chain().focus().insertContent(html).run();
  }

  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap gap-0.5 p-1.5 bg-zinc-800/80 border-b border-zinc-700">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strike"><Strikethrough size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code"><span className="font-mono text-[11px] font-bold px-1">&lt;/&gt;</span></Btn>
        <div className="w-px bg-zinc-700 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="H2"><Heading2 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="H3"><Heading3 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} active={editor.isActive('heading', { level: 4 })} title="H4"><span className="text-[11px] font-bold">H4</span></Btn>
        <div className="w-px bg-zinc-700 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list"><ListOrdered size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="HR">―</Btn>
        <div className="w-px bg-zinc-700 mx-1" />
        <Btn onClick={() => { const url = window.prompt('URL za link:'); if (url) editor.chain().focus().setLink({ href: url }).run(); }} active={editor.isActive('link')} title="Link"><LinkIcon size={15} /></Btn>
        {onInsertImage && <Btn onClick={onInsertImage} title="Slika iz medija"><ImageIcon size={15} /></Btn>}
        {enablePdf && (
          <>
            <Btn onClick={() => fileInputRef.current?.click()} title="PDF"><FileText size={15} /></Btn>
            <input ref={fileInputRef} type="file" accept="application/pdf" onChange={handlePdfUpload} className="hidden" data-testid="pdf-upload-input" />
          </>
        )}
        {enableColumns && (
          <>
            <div className="w-px bg-zinc-700 mx-1" />
            <Btn onClick={() => insertColumns(2)} title="2 stupca"><Columns2 size={15} /></Btn>
            <Btn onClick={() => insertColumns(3)} title="3 stupca"><Columns3 size={15} /></Btn>
            <Btn onClick={() => insertColumns(4)} title="4 stupca"><Columns4 size={15} /></Btn>
          </>
        )}
        {enableImprintWidget && (
          <>
            <div className="w-px bg-zinc-700 mx-1" />
            <Btn
              onClick={() => editor.chain().focus().insertContent({ type: 'imprintTree' }).run()}
              title="Umetni widget tima (Impressum)"
            >
              <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-1">
                <Users size={14} /> Tim
              </span>
            </Btn>
          </>
        )}
      </div>
      <EditorContent editor={editor} className="prose prose-invert prose-sm max-w-none p-3 min-h-[300px] [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[300px] [&_.em-columns]:grid [&_.em-columns]:gap-4 [&_.em-columns[data-cols='2']]:grid-cols-2 [&_.em-columns[data-cols='3']]:grid-cols-3 [&_.em-columns[data-cols='4']]:grid-cols-4 [&_.em-col]:border [&_.em-col]:border-zinc-700 [&_.em-col]:p-2 [&_.em-col]:rounded" />
    </div>
  );
}
